import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import axios from "axios";
import QRCode from "qrcode";
import { toast } from "react-toastify";
import { usePermissions } from "../login/PermissionContext";
import {
  fetchDoctorConsultationServiceRow,
  fetchFacilityDoctorsMap,
  formatInvoiceDateYmd,
  getSecuredApiHeaders,
  isInvoiceDateNotBeforeToday,
  doesServiceRowMatchVisitType,
  normalizeAppointmentVisitType,
  resolveAppointmentInvoiceItemId,
  resolveConsultationUnitPriceStr,
  safeInvoiceNumber,
} from "./appointmentInvoiceHelpers";
import { fetchViewInvoiceLocationState } from "./appointmentPaymentViewInvoiceState";
import AppointmentInvoicePreviewDialog from "./AppointmentInvoicePreviewDialog";

function getCurrentUserId() {
  try {
    const token = sessionStorage.getItem("accessToken") || "";
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    const raw = payload?.user_id ?? payload?.userId;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

/** Non-actionable from this modal — refund_approval.py/refund_payment.py on the
 * backend drive these transitions automatically once a refund is raised on the
 * Refund screen. "REFUND" is kept for appointments already carrying the legacy
 * manually-set value from before this flow existed. */
const REFUND_FLOW_STATUSES = [
  "INITIATE REFUND",
  "REFUNDED",
  "REFUND SETTLED",
  "REFUND REJECTED",
  "REFUND",
];

export default function PaymentModal({
  open,
  onClose,
  appointment,
  selectedDoctorData,
  onStatusUpdated,
  onAfterClose,
  /** When true, skip create_appointment_invoice on Mark as Paid (e.g. invoice already created at booking). */
  suppressAppointmentInvoiceBootstrap = false,
  /** Pre-fetched consultation service row (e.g. from DoctorSlotsModal) — skips get_medical_services/. */
  initialConsultationServiceRow = null,
}) {
  const { userInfo } = usePermissions();
  const [paymentType, setPaymentType] = useState("UPI");
  const [transactionId, setTransactionId] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [isRefundCompleted, setIsRefundCompleted] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [doctorFee, setDoctorFee] = useState("");
  const [facilityDoctorsMap, setFacilityDoctorsMap] = useState({});
  /** Resolved "{Doctor} Consultation fees" medical-service row — single source of
   * truth for both the on-screen amount/QR below and the invoice line price. */
  const [consultationServiceRow, setConsultationServiceRow] = useState(null);
  const [consultationServiceFetchState, setConsultationServiceFetchState] =
    useState("idle");
  const [qrUrl, setQrUrl] = useState("");

  const [bootstrapInvoiceNo, setBootstrapInvoiceNo] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLocationState, setPreviewLocationState] = useState(null);
  const [previewReceiptNo, setPreviewReceiptNo] = useState("");

  const facilityDoctorsMapRef = useRef(facilityDoctorsMap);
  const zeroFeeHandledRef = useRef(false);

  facilityDoctorsMapRef.current = facilityDoctorsMap;

  // Source UPI ID and facility identifier (used as payee name) from logged-in user info
  // Default UPI to test@123 when upi_id is null/undefined
  const upiId = userInfo?.upi_id;
  console.log("appointment--", appointment);
  console.log("upiId--", upiId);

  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const authToken = sessionStorage.getItem("accessToken") ?? "";
  const headers = { Authorization: `Bearer ${authToken}` };
  const securedHeaders = useMemo(
    () => getSecuredApiHeaders(authToken),
    [authToken]
  );

  const isUnpaidAppointment = useMemo(() => {
    const raw = appointment?.payment_status;
    const ps = (raw || "").toUpperCase();
    return raw == null || raw === "" || ps === "UNPAID";
  }, [appointment?.payment_status]);

  const currentDoctorId = useMemo(
    () =>
      appointment?.doctor_id ||
      selectedDoctorData?.doctor_id ||
      sessionStorage.getItem("doctor_id") ||
      null,
    [appointment?.doctor_id, selectedDoctorData?.doctor_id],
  );

  const appointmentVisitType = useMemo(
    () =>
      normalizeAppointmentVisitType(
        appointment?.appointment_type || "FIRST_VISIT",
      ),
    [appointment?.appointment_type],
  );

  const resolvedFeeStr = useMemo(() => {
    if (!consultationServiceRow) return null;
    const raw = consultationServiceRow?.price;
    if (raw == null || String(raw).trim() === "") return null;
    const fromService = Number(raw);
    // Explicit 0 is valid (Follow-up / Report Visit).
    return Number.isFinite(fromService) && fromService >= 0
      ? fromService.toFixed(2)
      : null;
  }, [consultationServiceRow]);

  const feeLoading =
    open &&
    isUnpaidAppointment &&
    resolvedFeeStr == null &&
    consultationServiceFetchState === "loading";

  const feeReady =
    !isUnpaidAppointment || resolvedFeeStr != null;

  const paymentAmountNum = safeInvoiceNumber(paymentAmount);
  const isZeroFee = feeReady && paymentAmountNum === 0;
  const isTxnRequired = paymentType !== "CASH" && paymentAmountNum > 0;

  useEffect(() => {
    if (!open) {
      setBootstrapInvoiceNo(null);
      setPreviewOpen(false);
      setPreviewLocationState(null);
      setPreviewReceiptNo("");
      setPaymentAmount("");
      setDoctorFee("");
      setTransactionId("");
      zeroFeeHandledRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (open && appointment) {
      const paid = (appointment?.payment_status || "").toUpperCase() === "PAID";
      setIsPaid(paid);
      // "REFUND" is the legacy manual-completion value; "REFUND SETTLED" is the
      // new value the backend sets once a refund payout actually settles.
      const refundCompleted = ["REFUND", "REFUND SETTLED"].includes(
        (appointment?.payment_status || "").toUpperCase()
      );
      setIsRefundCompleted(refundCompleted);
    }
  }, [open, appointment]);

  useEffect(() => {
    if (!open) return;
    if (!isUnpaidAppointment) {
      const paidAmt = appointment?.payment_amount;
      if (paidAmt != null && String(paidAmt).trim() !== "") {
        const s = Number(paidAmt).toFixed(2);
        setPaymentAmount(s);
        setDoctorFee(s);
      }
      return;
    }
    if (resolvedFeeStr != null) {
      setPaymentAmount(resolvedFeeStr);
      setDoctorFee(resolvedFeeStr);
    }
  }, [open, isUnpaidAppointment, appointment?.payment_amount, resolvedFeeStr]);

  useEffect(() => {
    const amt = safeInvoiceNumber(paymentAmount);
    if (open && paymentType === "UPI" && upiId && amt > 0 && !feeLoading) {
      const upiString = `upi://pay?pa=${encodeURIComponent(
        upiId,
      )}&cu=INR&am=${encodeURIComponent(amt.toFixed(2))}`;
      QRCode.toDataURL(upiString, { width: 256, margin: 1 })
        .then((url) => setQrUrl(url))
        .catch(() => setQrUrl(""));
    } else {
      setQrUrl("");
    }
  }, [open, paymentType, paymentAmount, upiId, feeLoading]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios
      .get(`${REACT_APP_BACKEND_ROUTE_URL}get_facility_specific_doctors/`, {
        headers,
      })
      .then((response) => {
        if (cancelled) return;
        if (
          response?.data?.status === "Success" &&
          Array.isArray(response.data.doctor_list)
        ) {
          const map = {};
          response.data.doctor_list.forEach((doc) => {
            if (doc?.doctor_id) map[doc.doctor_id] = doc;
          });
          setFacilityDoctorsMap(map);
        }
      })
      .catch(() => {
        // silent fail
      });
    return () => {
      cancelled = true;
    };
  }, [open, REACT_APP_BACKEND_ROUTE_URL]);

  // Resolve the doctor's fee service row for this visit type via doctor_id.
  // Prefer a prefetched row only when its name matches the visit-type fee suffix.
  useEffect(() => {
    if (!open || !isUnpaidAppointment) {
      setConsultationServiceRow(null);
      setConsultationServiceFetchState("idle");
      return;
    }

    const prefetched = initialConsultationServiceRow;
    const prefetchedRaw = prefetched?.price;
    const prefetchedPrice = Number(prefetchedRaw);
    const hasMatchingPrefetch =
      prefetched &&
      prefetchedRaw != null &&
      String(prefetchedRaw).trim() !== "" &&
      Number.isFinite(prefetchedPrice) &&
      prefetchedPrice >= 0 &&
      resolveAppointmentInvoiceItemId(prefetched) != null &&
      doesServiceRowMatchVisitType(prefetched, appointmentVisitType);

    if (hasMatchingPrefetch) {
      setConsultationServiceRow(prefetched);
      setConsultationServiceFetchState("done");
      return;
    }

    if (!currentDoctorId) {
      setConsultationServiceRow(null);
      setConsultationServiceFetchState("done");
      return;
    }

    let cancelled = false;
    setConsultationServiceFetchState("loading");

    (async () => {
      try {
        const row = await fetchDoctorConsultationServiceRow(
          axios,
          REACT_APP_BACKEND_ROUTE_URL,
          securedHeaders,
          currentDoctorId,
          appointmentVisitType,
        );
        if (cancelled) return;
        setConsultationServiceRow(row);
      } catch (err) {
        console.error("get_medical_services (resolve visit fee):", err);
        if (!cancelled) setConsultationServiceRow(null);
      } finally {
        if (!cancelled) setConsultationServiceFetchState("done");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    isUnpaidAppointment,
    currentDoctorId,
    appointmentVisitType,
    REACT_APP_BACKEND_ROUTE_URL,
    securedHeaders,
    initialConsultationServiceRow,
  ]);

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewLocationState(null);
    setPreviewReceiptNo("");
    if (typeof onClose === "function") {
      onClose();
    }
    if (typeof onAfterClose === "function") {
      onAfterClose();
    }
  };

  /** Fee ₹0: only flip appointment to PAID — no invoice, no payment_details. */
  const markZeroFeePaidAndClose = async () => {
    if (!appointment?.appointment_id) return false;
    setIsUpdatingPayment(true);
    try {
      const resp = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}change_payment_status/`,
        {
          appointment_id: appointment.appointment_id,
          payment_status: "PAID",
          payment_amount: "0.00",
          payment_mode: "CASH",
        },
        { headers: securedHeaders },
      );
      if (String(resp?.data?.status || "") !== "Success") {
        toast.error(
          resp?.data?.message || "Could not mark appointment as paid.",
        );
        return false;
      }
      setIsPaid(true);
      onStatusUpdated && onStatusUpdated("PAID", "0.00", "CASH");
      toast.success("Marked as paid (₹0 — no invoice required).");
      if (typeof onClose === "function") onClose();
      if (typeof onAfterClose === "function") onAfterClose();
      return true;
    } catch (err) {
      console.error("change_payment_status (zero fee):", err);
      toast.error(
        err?.response?.data?.message ||
          "Could not mark appointment as paid.",
      );
      return false;
    } finally {
      setIsUpdatingPayment(false);
    }
  };

  // If Process Payment opens for an unpaid ₹0 visit, auto-mark PAID and close
  // (no invoice / payment modal interaction).
  useEffect(() => {
    if (!open || !isUnpaidAppointment) return;
    if (resolvedFeeStr == null) return;
    if (safeInvoiceNumber(resolvedFeeStr) !== 0) return;
    if (zeroFeeHandledRef.current) return;
    zeroFeeHandledRef.current = true;
    markZeroFeePaidAndClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isUnpaidAppointment, resolvedFeeStr]);

  const updatePaymentStatus = async (nextPaid) => {
    try {
      if (!appointment) {
        setIsPaid(nextPaid);
        return;
      }
      if (nextPaid && isUnpaidAppointment && !feeReady) {
        toast.warning(
          feeLoading
            ? "Please wait for the consultation fee to load."
            : "Consultation fee is not available. Check doctor fees configuration.",
        );
        return;
      }
      if (nextPaid && isUnpaidAppointment && isZeroFee) {
        await markZeroFeePaidAndClose();
        return;
      }
      const doctorIdToUse =
        appointment.doctor_id ||
        (selectedDoctorData && selectedDoctorData.doctor_id) ||
        sessionStorage.getItem("doctor_id");
      if (!doctorIdToUse) {
        setIsPaid(nextPaid);
        return;
      }

      if (!nextPaid) {
        setIsUpdatingPayment(true);
        const payload = {
          appointment_id: appointment.appointment_id,
          payment_status: "UNPAID",
          payment_amount: paymentAmount,
          payment_mode: paymentType,
        };
        const resp = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}change_payment_status/`,
          payload,
          { headers: securedHeaders }
        );
        if (resp?.data?.status === "Success") {
          setIsPaid(false);
          onStatusUpdated &&
            onStatusUpdated(
              payload.payment_status,
              payload.payment_amount,
              payload.payment_mode
            );
        } else {
          toast.error(resp?.data?.message || "Could not update payment status");
        }
        return;
      }

      const currentUserId = getCurrentUserId();
      if (currentUserId == null) {
        toast.error("Unable to identify logged-in user. Please sign in again.");
        return;
      }

      if (isTxnRequired && !transactionId.trim()) {
        toast.error("Transaction ID is required for UPI/Card/Bank Transfer payments");
        return;
      }

      setIsUpdatingPayment(true);

      let invoiceNo =
        bootstrapInvoiceNo ||
        appointment?.invoice_number ||
        appointment?.invoice_no ||
        null;

      // Create appointment invoice on Mark as Paid (not on Process Payment open),
      // unless an invoice already exists (e.g. created at booking).
      if (
        isUnpaidAppointment &&
        !suppressAppointmentInvoiceBootstrap &&
        (!invoiceNo || String(invoiceNo).trim() === "")
      ) {
        const invoiceItemId =
          resolveAppointmentInvoiceItemId(consultationServiceRow);
        if (invoiceItemId == null) {
          toast.warning(
            "No consultation service exists for the selected doctor. Invoice was not created."
          );
          return;
        }

        const hip_id = sessionStorage.getItem("facility_name");
        const startRaw =
          appointment.start_time_ist ||
          appointment.start_time ||
          appointment.appointment_start_time;
        const invoice_date = formatInvoiceDateYmd(startRaw);

        if (!hip_id) {
          toast.warning(
            "Invoice was not created: facility is not configured for this session."
          );
          return;
        }
        if (!invoice_date) {
          toast.warning(
            "Invoice was not created: missing appointment start time on this record."
          );
          return;
        }
        if (!isInvoiceDateNotBeforeToday(invoice_date)) {
          toast.warning(
            "Invoice was not created: invoice date cannot be in the past."
          );
          return;
        }

        const doctorsArray = Object.values(facilityDoctorsMapRef.current || {});
        const priceStr = resolveConsultationUnitPriceStr(
          doctorIdToUse,
          doctorsArray,
          consultationServiceRow?.price,
        );
        if (!priceStr) {
          toast.warning(
            "Invoice was not created: consultation fee is not configured on the service catalog.",
          );
          return;
        }

        const patientId = appointment.patient;
        if (patientId == null || String(patientId).trim() === "") {
          toast.warning("Invoice was not created: patient id is missing.");
          return;
        }

        const invoiceBody = {
          hip_id: String(hip_id),
          invoice_date,
          patient_id: String(patientId),
          // Links the invoice line to the appointment so the backend can
          // auto-raise a refund if this paid appointment is later cancelled.
          appointment_id: appointment.appointment_id,
          note: "",
          gst_number: "",
          partial_payment: "0.00",
          tax: "0",
          item: {
            item_id: invoiceItemId,
            doctor_id: String(doctorIdToUse),
            quantity: 1,
            price: priceStr,
          },
        };

        let inv;
        try {
          const invRes = await axios.post(
            `${REACT_APP_BACKEND_ROUTE_URL}create_appointment_invoice/`,
            invoiceBody,
            {
              headers: {
                ...securedHeaders,
                "Content-Type": "application/json",
              },
            }
          );
          inv = invRes.data;
        } catch (invErr) {
          console.error("create_appointment_invoice (PaymentModal):", invErr);
          toast.error(
            invErr?.response?.data?.message ||
              invErr?.message ||
              "Invoice request failed."
          );
          return;
        }

        if (String(inv?.status || "").toLowerCase() !== "success") {
          toast.error(
            inv?.message ||
              "Invoice could not be created for this appointment."
          );
          return;
        }

        invoiceNo = inv.invoice_number || inv.invoice_no || null;
        if (!invoiceNo || String(invoiceNo).trim() === "") {
          toast.error(
            "Invoice was created but no invoice number was returned."
          );
          return;
        }
        setBootstrapInvoiceNo(invoiceNo);
        if (inv.invoice_number) {
          toast.success(`Invoice ${inv.invoice_number} created.`);
        }
      }

      if (!invoiceNo || String(invoiceNo).trim() === "") {
        toast.error(
          "Invoice number is missing. Unable to record payment."
        );
        return;
      }

      const careRaw = appointment?.care_context_id;
      const care_context_id =
        careRaw != null && String(careRaw).trim() !== ""
          ? String(careRaw).trim()
          : "N/A";

      const amt = Number(paymentAmount);
      const paymentAmountStr = Number.isFinite(amt)
        ? amt.toFixed(2)
        : String(paymentAmount || "0.00");

      const chPayload = {
        appointment_id: appointment.appointment_id,
        payment_status: "PAID",
        payment_amount: paymentAmountStr,
        payment_mode: paymentType,
      };
      const resp = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}change_payment_status/`,
        chPayload,
        { headers: securedHeaders }
      );
      if (String(resp?.data?.status || "") !== "Success") {
        toast.error(
          resp?.data?.message || "Could not update appointment payment status."
        );
        return;
      }

      const paymentDetailsPayload = {
        invoice_no: String(invoiceNo).trim(),
        payment_amount: paymentAmountStr,
        currency: "INR",
        care_context_id,
        mode_of_payment: paymentType,
        payment_type: "full",
        remarks: "Appointment payment",
        status: "PENDING",
        created_by: currentUserId,
        ...(transactionId.trim() && { transaction_ref_no: transactionId.trim() }),
      };

      const payResp = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}payment_details/`,
        paymentDetailsPayload,
        {
          headers: {
            ...securedHeaders,
            "Content-Type": "application/json",
          },
        }
      );

      if (String(payResp?.data?.status || "") !== "Success") {
        toast.error(payResp?.data?.message || "Payment recording failed");
        return;
      }

      const paymentData = payResp?.data?.data || {};
      const showPopup = payResp?.data?.show_popup;
      const isValid = payResp?.data?.is_valid !== false;

      if (!isValid) {
        toast.warning(
          payResp?.data?.message || "Payment response was not marked valid."
        );
      }

      setIsPaid(true);
      onStatusUpdated &&
        onStatusUpdated("PAID", paymentAmountStr, paymentType);

      const wantPreview =
        showPopup !== false &&
        showPopup !== "false" &&
        String(showPopup).toLowerCase() !== "false";

      if (wantPreview) {
        try {
          const locState = await fetchViewInvoiceLocationState({
            baseUrl: REACT_APP_BACKEND_ROUTE_URL,
            headers: securedHeaders,
            invoiceNo: String(invoiceNo).trim(),
            payment: paymentData,
            paymentAmount: paymentAmountStr,
            paymentType,
            invoiceDataFallback: {
              patient_id: appointment.patient,
              patient_name:
                appointment.patient_name ||
                appointment.patientName ||
                "N/A",
            },
            serviceMaster: [],
            patientMaster: [],
          });
          setPreviewReceiptNo(
            paymentData?.receipt_number ||
              paymentData?.receipt_no ||
              ""
          );
          setPreviewLocationState(locState);
          setPreviewOpen(true);
          toast.success(
            payResp?.data?.message || "Payment completed successfully."
          );
        } catch (e) {
          console.error(e);
          toast.success(
            payResp?.data?.message || "Payment completed successfully."
          );
          if (typeof onClose === "function") {
            onClose();
          }
          if (typeof onAfterClose === "function") {
            onAfterClose();
          }
        }
      } else {
        toast.success(
          payResp?.data?.message || "Payment completed successfully."
        );
        if (typeof onClose === "function") {
          onClose();
        }
        if (typeof onAfterClose === "function") {
          onAfterClose();
        }
      }
    } catch (e) {
      setIsPaid((prev) => !nextPaid);
      toast.error(
        e?.response?.data?.message ||
          e?.message ||
          "Payment update failed"
      );
    } finally {
      setIsUpdatingPayment(false);
    }
  };
  const handleClose = () => {
    if (previewOpen) {
      handlePreviewClose();
      return;
    }
    if (typeof onClose === "function") {
      onClose();
    }
    if (typeof onAfterClose === "function") {
      onAfterClose();
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          width: "90%",
          maxWidth: "600px",
          // height: "80vh",
          height: "auto",
          zIndex: 9997,
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#213360",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1.5,
          px: 2,
        }}
      >
        <div
          className="main-title"
          style={{
            textAlign: "center",
            color: "#FFFFFF",
            margin: 0,
            fontSize: "18px",
            fontWeight: 600,
          }}
        >
          Process Payment
        </div>
        <IconButton onClick={handleClose}>
          <CloseRoundedIcon
            sx={{
              color: "#FFFFFF",
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
            }}
          />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ p: 2.5, maxHeight: "calc(80vh - 100px)", overflowY: "auto" }}
      >
        {isUnpaidAppointment && isZeroFee ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              py: 4,
            }}
          >
            <CircularProgress size={36} />
            <Typography sx={{ color: "#6b7280", fontSize: 14 }}>
              No fee due (₹0) — marking appointment as paid…
            </Typography>
          </Box>
        ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {appointment?.payment_status === null ||
          appointment?.payment_status === "" ||
          appointment?.payment_status === "UNPAID" ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                }}
              >
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      color: "#1c2c4c",
                      fontWeight: 600,
                    }}
                  >
                    Payment Type
                  </label>
                  <select
                    value={paymentType}
                    onChange={(e) => setPaymentType(e.target.value)}
                    className="form-control"
                    style={{ maxWidth: 280 }}
                  >
                    <option value="UPI">UPI</option>
                    <option value="CASH">Cash</option>
                    <option value="CREDIT CARD">Credit Card</option>
                    <option value="DEBIT CARD">Debit Card</option>
                    {/* <option value="BANK TRANSFER">Bank Transfer</option> */}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      color: "#1c2c4c",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Payment Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={feeLoading ? "" : paymentAmount}
                    placeholder={feeLoading ? "Loading fee…" : "—"}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="form-control"
                    style={{ maxWidth: 280 }}
                    disabled
                    readOnly
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      color: "#1c2c4c",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      marginBottom: "20px",
                    }}
                  >
                    Payment Status
                  </label>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "999px",
                        fontSize: 12,
                        fontWeight: 600,
                        color:
                          isPaid || isRefundCompleted ? "#0b3d0b" : "#5D3A00",
                        backgroundColor:
                          isPaid || isRefundCompleted ? "#A1DA3B" : "#FFBD8A",
                        border: "1px solid #BAE0DD",
                        textTransform: "capitalize",
                        width: "fit-content",
                        marginBottom: "10px",
                      }}
                    >
                      {/* {isPaid ? "paid" : "unpaid"} */}

                      {appointment?.payment_status === "PAID"
                        ? "Paid"
                        : appointment?.payment_status === "INITIATE REFUND"
                        ? "Refund Initiated"
                        : appointment?.payment_status === "REFUNDED"
                        ? "Refund Approved"
                        : appointment?.payment_status === "REFUND SETTLED"
                        ? "Refund Completed"
                        : appointment?.payment_status === "REFUND REJECTED"
                        ? "Refund Rejected"
                        : appointment?.payment_status === "REFUND"
                        ? "Refund Completed"
                        : "Unpaid"}
                    </Box>
                  </Box>
                </div>
              </Box>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: 6,
                    color: "#1c2c4c",
                    fontWeight: 600,
                  }}
                >
                  Transaction ID{" "}
                  {isTxnRequired && <span style={{ color: "red" }}>*</span>}
                </label>
                <input
                  type="text"
                  placeholder={
                    isTxnRequired
                      ? "Enter Transaction ID / UTR / Ref No."
                      : "Transaction ID (optional)"
                  }
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  className="form-control"
                  style={{ maxWidth: 400 }}
                />
              </div>

              {paymentType === "UPI" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: { xs: "center", sm: "flex-start" },
                    gap: 3,
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    p: 2.5,
                    mt: 1,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  {upiId === null || upiId === undefined ? (
                    <div
                      style={{
                        width: 160,
                        height: 160,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f3f4f6",
                        color: "#6b7280",
                        borderRadius: "6px",
                        textAlign: "center",
                        fontSize: "13px",
                      }}
                    >
                      QR Code Not Available
                    </div>
                  ) : feeLoading ? (
                    <div
                      style={{
                        width: 160,
                        height: 160,
                        flexShrink: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        background: "#f3f4f6",
                        color: "#6b7280",
                        borderRadius: "6px",
                      }}
                    >
                      <CircularProgress size={28} />
                      <span style={{ fontSize: 12 }}>Loading fee…</span>
                    </div>
                  ) : qrUrl ? (
                    <img
                      src={qrUrl}
                      alt="UPI QR Code"
                      style={{
                        width: 160,
                        height: 160,
                        flexShrink: 0,
                        borderRadius: "6px",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 160,
                        height: 160,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f3f4f6",
                        color: "#6b7280",
                        borderRadius: "6px",
                        textAlign: "center",
                        fontSize: 12,
                        padding: 8,
                      }}
                    >
                      {feeReady
                        ? isZeroFee
                          ? "No payment due (₹0)"
                          : "Generating QR…"
                        : "Fee unavailable for QR"}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      className="patient-name"
                      style={{
                        marginBottom: "10px",
                        fontWeight: 600,
                        color: "#1c2c4c",
                      }}
                    >
                      {isZeroFee
                        ? "No consultation fee due"
                        : "Pay consultation fee via UPI"}
                    </div>
                    <div
                      className="patient-detail"
                      style={{ marginBottom: "6px" }}
                    >
                      UPI ID: {upiId || "Not Available"}
                    </div>
                    <div className="patient-detail">
                      Amount:{" "}
                      {feeLoading ? (
                        <span style={{ color: "#6b7280" }}>
                          Loading consultation fee…
                        </span>
                      ) : feeReady ? (
                        `₹${paymentAmount}`
                      ) : (
                        <span style={{ color: "#b45309" }}>
                          Consultation fee could not be loaded
                        </span>
                      )}
                    </div>
                  </div>
                </Box>
              )}
            </>
          ) : appointment?.payment_status === "PAID" ? (
            <>
              <Typography>
                This appointment has already been paid, and the payment of Rs.
                {paymentAmount} has been successfully recorded. If this was
                marked as paid by mistake, you can click on ‘Mark as Unpaid’ to
                correct it.
              </Typography>
            </>
          ) : appointment?.payment_status === "INITIATE REFUND" ? (
            <>
              <Typography>
                A refund has been raised for this appointment and is awaiting
                approval on the Refund screen. This status updates automatically
                once the refund is approved and paid out — no action is needed
                here.
              </Typography>
            </>
          ) : appointment?.payment_status === "REFUNDED" ? (
            <>
              <Typography>
                The refund for this appointment has been approved and is
                awaiting payout on the Refund screen. This status updates
                automatically once the payout is completed.
              </Typography>
            </>
          ) : appointment?.payment_status === "REFUND REJECTED" ? (
            <>
              <Typography>
                The refund request for this appointment was rejected.
              </Typography>
            </>
          ) : (
            <>
              <Typography>
                The refund process is successfully completed.
              </Typography>
            </>
          )}
        </Box>
        )}
      </DialogContent>
      <DialogActions>
        {" "}
        {!REFUND_FLOW_STATUSES.includes(appointment?.payment_status) &&
        !(isUnpaidAppointment && isZeroFee) ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 1,
            }}
          >
            <button
              type="button"
              onClick={() => updatePaymentStatus(!isPaid)}
              className="btn btn__secondary btn__rounded"
              disabled={
                isUpdatingPayment ||
                (!isPaid && isUnpaidAppointment && !feeReady)
              }
              title={
                !isPaid && isUnpaidAppointment && !feeReady
                  ? feeLoading
                    ? "Waiting for consultation fee"
                    : "Consultation fee is required before marking as paid"
                  : undefined
              }
            >
              {isUpdatingPayment
                ? "Updating..."
                : isPaid
                ? "Mark as Unpaid"
                : "Mark as Paid"}
            </button>
          </Box>
        ) : null}
      </DialogActions>
    </Dialog>
    <AppointmentInvoicePreviewDialog
      open={previewOpen}
      onClose={handlePreviewClose}
      invoiceNo={
        bootstrapInvoiceNo ||
        appointment?.invoice_number ||
        appointment?.invoice_no ||
        ""
      }
      receiptNo={previewReceiptNo}
      locationState={previewLocationState}
    />
    </>
  );
}
